/**
 * Les types du domaine Dispatch.
 *
 * Ils suivent EXACTEMENT les colonnes Postgres. C'est ici que se joue l'intérêt
 * de la bascule : la console HTML lisait des colonnes sans qu'aucun outil ne
 * vérifie leur existence. Une faute de frappe donnait `undefined`, qui
 * s'affichait « 0 FCFA » ou « — » sans la moindre erreur.
 *
 * Tous les montants sont des ENTIERS en francs CFA. Le FCFA n'a pas de
 * subdivision en circulation, et un flottant introduirait des arrondis là où la
 * comptabilité SYSCOHADA n'en tolère aucun.
 */

// ── Commandes ────────────────────────────────────────────────────────────

/** ⚠️ Les DIX valeurs de l'enum Postgres `order_status`. */
export type StatutCommande =
  | 'pending'
  | 'paid'
  | 'accepted'
  | 'preparing'
  | 'ready'
  | 'rider_assigned'
  | 'picked_up'
  | 'delivering'
  | 'delivered'
  | 'cancelled'

/* `Record<StatutCommande, …>` EXIGE les dix clés : oublier une valeur ne
   compile plus. C'est exactement ce qui manquait à la console Marchand, où
   deux statuts s'affichaient « undefined ». */
export const LIBELLE_STATUT: Record<StatutCommande, string> = {
  pending: 'Attente paiement',
  paid: 'Payée',
  accepted: 'Acceptée',
  preparing: 'En préparation',
  ready: 'Prête',
  rider_assigned: 'Livreur assigné',
  picked_up: 'Récupérée',
  delivering: 'En livraison',
  delivered: 'Livrée',
  cancelled: 'Annulée',
}

/**
 * La COULEUR du badge, reprise de la légende « Parcours d'une commande » de la
 * console d'origine.
 *
 * ⚠️ La feuille de style définit six variantes (`badge-gray`, `-blue`,
 * `-orange`, `-yellow`, `-green`, `-red`). N'écrire que `badge` — ce que
 * faisait le premier portage — les rend toutes identiques et fait perdre
 * l'information la plus utile de l'écran : où en est chaque commande, d'un
 * coup d'œil.
 */
export const BADGE_STATUT: Record<StatutCommande, string> = {
  pending: 'badge-gray',
  paid: 'badge-blue',
  accepted: 'badge-blue',
  preparing: 'badge-orange',
  ready: 'badge-yellow',
  rider_assigned: 'badge-blue',
  picked_up: 'badge-orange',
  delivering: 'badge-orange',
  delivered: 'badge-green',
  cancelled: 'badge-red',
}

export const libelleStatut = (s: string): string =>
  LIBELLE_STATUT[s as StatutCommande] ?? String(s || '—')

export const badgeStatut = (s: string): string =>
  BADGE_STATUT[s as StatutCommande] ?? 'badge-gray'

/** Les statuts qui occupent encore le dispatch. Le reste est de l'archive. */
export const STATUTS_ACTIFS: StatutCommande[] = [
  'paid',
  'accepted',
  'preparing',
  'ready',
  'rider_assigned',
  'picked_up',
  'delivering',
]

export interface ArticleCommande {
  name: string
  quantity: number
  /** Prix CLIENT unitaire du plat (hors suppléments). */
  unit_price: number
  /** Total CLIENT de la ligne. */
  total: number | null
  commission_pct: number | null
  /** Modèle « commission ajoutée » (0055) ; absents/NULL = ancien modèle (commission retenue). */
  prix_marchand_unitaire?: number | null
  montant_marchand?: number | null
  montant_commission?: number | null
}

export interface Horodatages {
  accepted: string | null
  preparing: string | null
  ready: string | null
  rider_assigned: string | null
  delivering: string | null
  picked_up: string | null
  delivered: string | null
  cancelled: string | null
}

export interface Commande {
  id: string
  ref: string | null
  statut: StatutCommande
  total: number
  /**
   * Horodatage de CHAQUE étape. Déjà ramenés par la requête mais jamais
   * exposés : le suivi pas à pas de la fiche commande était donc impossible à
   * construire, alors que c'est ce qui permet de répondre à « pourquoi cette
   * commande a pris deux heures ».
   */
  accepteeLe: string | null
  enPreparationLe: string | null
  preteLe: string | null
  livreurAffecteLe: string | null
  /** Retrait chez le marchand — début du délai de livraison réel. */
  retireeLe: string | null
  enLivraisonLe: string | null
  livreeLe: string | null
  annuleeLe: string | null
  fraisLivraison: number
  /**
   * ⚠️ `driver_pay` ≠ `delivery_fee`. Le premier est ce que touche le livreur,
   * le second ce que paie le client. L'écart EST la marge sur la livraison.
   * Les confondre fausse à la fois la paie et la comptabilité.
   */
  partLivreur: number | null
  fraisService: number
  /** Supplément « petite commande ». Produit accessoire (707), pas une pénalité. */
  fraisPetiteCommande: number
  /** Supplément météo payé par le client — distinct du bonus versé au livreur. */
  fraisMeteo: number
  /** Majoration « Forte demande » payée par le client (0054, `tension_fee`) — produit de livraison (706). */
  fraisTension?: number
  /** Dont part livreur de la majoration — DÉJÀ comprise dans `partLivreur` (`tension_driver_pay`). */
  partTensionLivreur?: number
  /**
   * Pourboire : payé par le client, reversé EN ENTIER au livreur dans sa paie
   * (0019). Ni produit ni charge pour nous — un transit, comme la part marchand.
   */
  pourboire?: number
  /** Montant remboursable fixé à l'annulation (`refund_amount`, 0015). */
  montantRembourse?: number | null
  clientId: string | null
  clientNom: string
  clientTelephone: string
  clientQuartier: string
  clientLat: number | null
  clientLng: number | null
  adresseLivraison: string
  communeLivraison: string
  noteLivraison: string
  marchandId: string | null
  marchandNom: string
  marchandLat: number | null
  marchandLng: number | null
  livreurId: string | null
  livreurNom: string | null
  articles: ArticleCommande[]
  creeeLe: string
  horodatages: Horodatages
}

// ── Livreurs ─────────────────────────────────────────────────────────────

/**
 * L'état affiché au dispatch, réduit à trois valeurs.
 *
 * La base en connaît plus (`available`, `busy`, `on_delivery`, `offline`) ;
 * `busy` et `on_delivery` disent la même chose au dispatch — le livreur n'est
 * pas assignable.
 */
export type EtatLivreur = 'online' | 'busy' | 'offline'

export function etatLivreur(brut: string | null): EtatLivreur {
  if (brut === 'available') return 'online'
  if (brut === 'busy' || brut === 'on_delivery') return 'busy'
  return 'offline'
}

export interface Livreur {
  id: string
  nom: string
  telephone: string
  typeVehicule: string | null
  plaque: string
  modele: string
  fournisseurPaie: 'wave' | 'om'
  numeroPaie: string
  etat: EtatLivreur
  /** `drivers.rating` : moyenne des seuls avis réels (maintenue par la migration 0048). */
  note: number | null
  /**
   * Note MIXTE (avis réels + note auto pour les livraisons sans avis), lue dans la
   * vue `livreur_notes` (0048). `null` = pas encore de note, OU vue absente (alors
   * `avis` vaut aussi `null`).
   */
  noteMixte: number | null
  /** Avis clients avec une note livreur ; `null` tant que la vue n'est pas en ligne. */
  avis: number | null
  /** Livraisons sans note, comptées à la note auto. */
  sansAvis: number | null
  livraisons: number
  verifie: boolean
  creeLe: string
  lat: number
  lng: number
}

// ── Marchands ────────────────────────────────────────────────────────────

export type EtatMarchand = 'ouvert' | 'ferme' | 'bloque'

export interface Marchand {
  id: string
  nom: string
  type: 'restaurant' | 'supermarche'
  quartier: string
  etat: EtatMarchand
  motifBlocage: string | null
  pauseJusqua: string | null
  note: number | null
  telephone: string
  description: string
  creeLe: string
  lat: number
  lng: number
  /**
   * Faux tant que le point de retrait n'a jamais été posé. ⚠️ `lat`/`lng`
   * valent alors 0 (golfe de Guinée) : sans position, `create-order` compte une
   * distance de 0 km et applique en silence le palier de livraison le moins
   * cher, et le livreur ne sait pas où récupérer la commande.
   */
  positionConnue: boolean
  /** Rubriques de l'app client (`merchant_categories.id`). Vide = visible sous « Tout » seulement. */
  categories: number[]
  /**
   * ⚠️ L'identifiant de connexion du gérant vit dans `profiles`, PAS dans
   * `merchants.email` — `create-merchant-account` n'écrit que le profil, et la
   * colonne `merchants.email` est vide sur tous les établissements. La lire
   * afficherait « aucun compte » y compris pour ceux qui en ont un.
   */
  email: string
  /** Numéro du gérant (E.164) — son identifiant quand le compte n'a pas d'e-mail. */
  telephoneGerant: string
  nomContact: string
  /** Profil du gérant = pair pour le chat. `null` si le compte n'existe pas encore. */
  proprietaireId: string | null
}

// ── Clients ──────────────────────────────────────────────────────────────

/**
 * Un client est un COMPTE, pas la trace de ses achats.
 *
 * L'annuaire se construisait auparavant à partir des commandes : un client
 * inscrit qui n'avait pas encore commandé n'existait pour personne, et si le
 * chargement des commandes échouait, l'annuaire entier disparaissait sans un mot.
 */
export interface Client {
  id: string
  nom: string
  email: string
  telephone: string
  creeLe: string
}

// ── Paiements et remboursements ──────────────────────────────────────────

export interface Paiement {
  id: string
  ref: string
  refCommande: string
  commandeId: string | null
  clientNom: string
  fournisseur: 'wave' | 'om'
  montant: number
  statut: string
  creeLe: string
}

/**
 * Remboursement DÛ — commande annulée après paiement.
 *
 * ⚠️ Aucun remboursement n'est exécuté automatiquement : il n'existe pas d'API
 * de remboursement Wave ou Orange branchée. L'opérateur rembourse à la main,
 * puis marque ici — c'est seulement à ce moment que le paiement passe à
 * `refunded`. Automatiser l'affichage sans automatiser le virement ferait
 * croire à un remboursement qui n'a pas eu lieu.
 */
export interface Remboursement {
  commandeId: string
  ref: string | null
  client: string
  total: number
  montant: number
  annulePar: string | null
  motif: string | null
  annuleLe: string | null
  paiementId: string | null
  fournisseur: string
  fait: boolean
  payable: boolean
}

// ── Paie ─────────────────────────────────────────────────────────────────

export interface PaieLivreur {
  livreurId: string
  livreurNom: string
  courses: number
  base: number
  bonusMeteo: number
  bonusPointe: number
  bonusVolume: number
  bonusFidelite: number
  /** Détail des primes (0060) : courses en pointe, jours au seuil de volume, mois de fidélité versés. */
  coursesPointe: number
  joursVolume: number
  moisFidelite: number
  pourboires: number
  total: number
  /**
   * ⚠️ La période était perdue à la conversion dans la version HTML : l'en-tête
   * de l'écran la recalculait depuis l'horloge du NAVIGATEUR et pouvait donc
   * annoncer une quinzaine au-dessus des chiffres d'une autre.
   */
  debut: string
  fin: string
  statut: 'paye' | 'en attente'
}

export interface PeriodePayee {
  periode: string
  livreurs: number
  total: number
  /** Primes versées sur la période (0060). */
  bonusPointe: number
  bonusVolume: number
  bonusFidelite: number
}

// ── Messagerie ───────────────────────────────────────────────────────────

export interface MessageChat {
  id: string
  de: 'dispatch' | 'them' | 'system'
  texte: string
  envoyeLe: string
  lu: boolean
}

/** Les fils d'assistance, indexés par `cleFil(personne, casquette)` (migration 0053). */
export type FilsParPair = Record<string, MessageChat[]>

// ── Configuration ────────────────────────────────────────────────────────

export interface PalierLivraison {
  /** Identifiant `serial` de la base (nombre), transporté en texte. */
  id: string
  libelle: string
  /** Distance mini (incluse) et maxi (exclue), en km, une décimale. */
  minKm: number
  maxKm: number
  /** Ce que paie le client. */
  fraisClient: number
  /** Ce que touche le livreur. L'écart avec le tarif client EST la marge. */
  partLivreur: number
  /** Ce que paie un client Premium (0 = livraison offerte). */
  fraisPremium: number
}

export interface BonusMeteo {
  id: string
  /** Valeur de l'enum `weather_condition` : normal | rain | extreme_heat. */
  condition: string
  bonusLivreur: number
  supplementClient: number
  /** Les abonnés Premium ne paient pas le supplément client. */
  premiumExempt: boolean
}

export interface Configuration {
  fraisService: number
  seuilPetiteCommande: number
  fraisPetiteCommande: number
  seuilLivraisonOfferte: number
  /** « hh:mm-hh:mm ». Postgres renvoie « 12:00:00 » : la troncature est nécessaire,
   *  sinon ré-enregistrer une plage inchangée est refusé pour cause de format. */
  pointeMidi: string
  pointeSoir: string
  bonusPointe: number
  seuilVolume: number
  bonusVolume: number
  seuilFidelite: number
  bonusFidelite: number
  premiumMensuel: number
  jourPaie1: number
  jourPaie2: number
  /** `auto_rating` : note (1 à 5) attribuée à une livraison que le client n'a pas notée. */
  noteAuto: number
  /** Affiché en POURCENTAGE (la base stocke une fraction). */
  commissionParDefaut: number
  /** Assujettissement à la TVA. Sous le seuil de CA, une entreprise malienne
   *  relève de l'impôt synthétique et ne facture pas de taxe. */
  tvaActive: boolean
  /** Affiché en POURCENTAGE, comme la commission (la base stocke 0.18). */
  tvaTaux: number
  /**
   * Frais des opérateurs mobile money, en POURCENTAGE (migration 0044).
   * `null` = pas encore connu (contrats non signés) ; `undefined` = la colonne
   * n'existe pas encore en base.
   */
  fraisMM: {
    encaissementWave: number | null | undefined
    encaissementOm: number | null | undefined
    decaissementWave: number | null | undefined
    decaissementOm: number | null | undefined
  }
  paliersLivraison: PalierLivraison[]
  bonusMeteo: BonusMeteo[]
  /**
   * Météo EN COURS, décidée par le dispatch (migration 0043). `null` = la
   * colonne n'existe pas encore en production : l'écran le dit au lieu de
   * montrer un sélecteur qui n'écrirait nulle part.
   */
  meteoActuelle: 'normal' | 'rain' | 'extreme_heat' | null
  /** Suppléments « traversée du Niger » (0043). `null` = colonnes absentes. */
  fraisTraversee: number | null
  bonusTraversee: number | null
  /** Prix à 0 km du barème progressif (0054). `null` = colonnes absentes (base pas à jour). */
  minimaLivraison: { client: number; premium: number; driver: number } | null
  /** Frais de service à la distance max de la grille (0054) ; `null` = frais fixes (`fraisService`). */
  fraisServiceHaut: number | null
  /** Majoration de tension (0054). `null` = colonnes absentes. */
  tension: ReglagesTensionConfig | null
  /** Réduction Premium sur la livraison, en % (0055). `null` = colonne absente. */
  reductionPremium: number | null
  /** Sous-total client minimum pour commander (0055). `null` = colonne absente. */
  commandeMinimum: number | null
}

export interface ReglagesTensionConfig {
  active: boolean
  rayonKm: number
  seuil: number
  pentePct: number
  plafondPct: number
  partLivreurPct: number
}

// ── Équipe ───────────────────────────────────────────────────────────────

export interface MembreStaff {
  id: string
  profileId: string
  nom: string
  email: string
  role: string
  statut: string
  creeLe: string
  /** Intitulé de poste — libre, purement informatif (« Dispatch nuit »). */
  intitule: string
  /** Numéro au format E.164, vide s'il n'y en a pas (compte créé avec un e-mail seul). */
  telephone: string
  /**
   * ÉCARTS au rôle, pas les droits complets : seules les clés qui diffèrent du
   * rôle sont stockées. Un écart peut aussi bien retirer qu'ajouter.
   */
  permissions: Record<string, boolean>
}
